import { useTranslator } from "@sunrise/translator";
import { ReactNode } from "react";

import { FocusButton } from "@/components";
import { useDetailsSeriesSort } from "@/modules/details/use-details-series-sort";

import * as styles from "./details-series-actions.css";
import { DetailsSeriesFilter } from "./details-series-filter";
import type { AssetId, RecordingGroupId } from "@sunrise/backend-types-core";

const TEST_ID = "details-series-actions";

type DetailsSeriesActionsProps = {
  seriesAssetId: AssetId;
  recordingGroupId: RecordingGroupId;
  onCancelRecordings: () => void;
  onDeleteRecordings: () => void;
  haveRecordingSchedules: boolean;
  haveRecordings: boolean;
  seasonButtonFocusKey: string;
  deleteButtonFocusKey: string;
  cancelButtonFocusKey: string;
  sortButtonFocusKey: string;
};

export function DetailsSeriesActions({
  onCancelRecordings,
  onDeleteRecordings,
  seriesAssetId,
  recordingGroupId,
  haveRecordingSchedules,
  haveRecordings,
  cancelButtonFocusKey,
  deleteButtonFocusKey,
  seasonButtonFocusKey,
  sortButtonFocusKey,
}: DetailsSeriesActionsProps): ReactNode {
  const t = useTranslator();

  const openSortRecordings = useDetailsSeriesSort({
    seriesAssetId,
    recordingGroupId,
  });

  return (
    <div className={styles.root}>
      <DetailsSeriesFilter
        recordingGroupId={recordingGroupId}
        seriesAssetId={seriesAssetId}
        focusKey={seasonButtonFocusKey}
        data-testid={`${TEST_ID}.season-filter`}
      />
      <div className={styles.right}>
        <FocusButton
          disabled={!haveRecordingSchedules}
          focusKey={cancelButtonFocusKey}
          className={styles.button}
          onEnterPress={onCancelRecordings}
          data-testid={`${TEST_ID}.cancel-recordings`}
          text={t("button_cancel_recordings")}
        />
        <FocusButton
          disabled={!haveRecordings}
          focusKey={deleteButtonFocusKey}
          onEnterPress={onDeleteRecordings}
          className={styles.button}
          data-testid={`${TEST_ID}.delete-recordings`}
          text={t("recordings_delete_all")}
        />
        {haveRecordings && (
          <FocusButton
            className={styles.button}
            focusKey={sortButtonFocusKey}
            data-testid={`${TEST_ID}.change-sort`}
            text={t("recording_sort")}
            onEnterPress={openSortRecordings}
            iconLeft="sort"
          />
        )}
      </div>
    </div>
  );
}
